// Service and maintenance on the web (CR_20260921_Asset_Lifecycle T3). Rules per category (managers write), the
// computed status per asset (public.asset_service_status — next due is never stored), the work (read like the asset,
// written by log_service: a manager or warehouse staff at the asset's site, §4.5) and its cost — asset_service_costs,
// which RLS returns to managers only, so for anyone else the cost is simply absent (L19).
import { DOC_BUCKET, docPath } from '@fleetspoke/core/lifecycle';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { lifecycleKeys } from './lifecycle';

export interface ServiceRule {
  id: string;
  category_id: string;
  task: string;
  interval_days: number | null;
  interval_deployed_days: number | null;
}

export interface ServiceStatus {
  asset_id: string;
  rule_id: string;
  task: string;
  interval_days: number | null;
  interval_deployed_days: number | null;
  last_done_on: string | null;
  due_on: string | null;
  state: 'ok' | 'due_soon' | 'overdue';
}

export type ServiceKind = 'service' | 'repair' | 'inspection';
export const SERVICE_KINDS: readonly ServiceKind[] = ['service', 'repair', 'inspection'];

export interface ServiceRecord {
  id: string;
  kind: ServiceKind;
  task: string | null;
  done_on: string;
  supplier_name: string | null;
  note: string | null;
  document_path: string | null;
  /** managers only — absent for anyone else (RLS) */
  cost: { amount: number; currency: string } | null;
}

export const serviceKeys = {
  all: ['service'] as const,
  rules: ['service', 'rules'] as const,
  asset: (assetId: string) => ['service', 'asset', assetId] as const,
};

function fail(error: { message: string; code?: string } | null) {
  if (error) throw Object.assign(new Error(error.message), { code: error.code });
}

export function useServiceRules() {
  return useQuery({
    queryKey: serviceKeys.rules,
    queryFn: async () => {
      const { data, error } = await supabase()
        .from('category_service_rules')
        .select('id, category_id, task, interval_days, interval_deployed_days')
        .order('task');
      fail(error);
      return (data ?? []) as ServiceRule[];
    },
  });
}

/** The asset's rules with last done / next due, and its last few records (with cost for managers). */
export function useAssetService(assetId: string) {
  return useQuery({
    queryKey: serviceKeys.asset(assetId),
    enabled: !!assetId,
    queryFn: async () => {
      const [status, records] = await Promise.all([
        supabase().from('asset_service_status').select('*').eq('asset_id', assetId).order('task'),
        supabase()
          .from('asset_service_records')
          .select('id, kind, task, done_on, supplier_name, note, document_path')
          .eq('asset_id', assetId)
          .order('done_on', { ascending: false })
          .order('created_at', { ascending: false })
          .limit(5),
      ]);
      fail(status.error);
      fail(records.error);
      const recs = (records.data ?? []) as Omit<ServiceRecord, 'cost'>[];
      const costs = new Map<string, { amount: number; currency: string }>();
      if (recs.length) {
        // zero rows for anyone but a manager — the cost is simply not there (L19)
        const c = await supabase()
          .from('asset_service_costs')
          .select('asset_service_record_id, amount, currency')
          .in(
            'asset_service_record_id',
            recs.map((r) => r.id),
          );
        fail(c.error);
        for (const row of (c.data ?? []) as {
          asset_service_record_id: string;
          amount: number;
          currency: string;
        }[])
          costs.set(row.asset_service_record_id, {
            amount: Number(row.amount),
            currency: row.currency,
          });
      }
      return {
        status: (status.data ?? []) as ServiceStatus[],
        records: recs.map((r): ServiceRecord => ({ ...r, cost: costs.get(r.id) ?? null })),
      };
    },
  });
}

function useRefresh(assetId?: string) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: serviceKeys.all });
    if (assetId) void queryClient.invalidateQueries({ queryKey: lifecycleKeys.asset(assetId) });
  };
}

export interface LogServiceInput {
  kind: ServiceKind;
  task: string;
  doneOn: string;
  supplier: string;
  note: string;
  document: File | null;
  /** managers only; written after the record (a separate, managers-only table) */
  cost: string;
  currency: string;
}

export function useLogService(companyId: string, assetId: string) {
  const refresh = useRefresh(assetId);
  return useMutation({
    mutationFn: async (s: LogServiceInput) => {
      let path: string | null = null;
      if (s.document) {
        path = docPath(companyId, 'service', crypto.randomUUID(), s.document.type);
        const up = await supabase()
          .storage.from(DOC_BUCKET)
          .upload(path, s.document, { contentType: s.document.type, upsert: false });
        if (up.error) throw new Error(up.error.message);
      }
      const { data, error } = await supabase().rpc('log_service', {
        p_idempotency_key: crypto.randomUUID(),
        p_asset_id: assetId,
        p_kind: s.kind,
        p_done_on: s.doneOn,
        p_task: s.task.trim() || null,
        p_supplier_name: s.supplier.trim() || null,
        p_note: s.note.trim() || null,
        p_document_path: path,
      });
      if (error) {
        if (path) await supabase().storage.from(DOC_BUCKET).remove([path]);
        throw Object.assign(new Error(error.message), { code: error.code });
      }
      const recordId = (data as { record_id: string }).record_id;
      if (s.cost.trim() !== '') {
        const c = await supabase()
          .from('asset_service_costs')
          .insert({
            company_id: companyId,
            asset_service_record_id: recordId,
            amount: Number(s.cost),
            currency: s.currency || 'MYR',
          });
        fail(c.error);
      }
      return recordId;
    },
    onSettled: refresh,
  });
}

export function useSaveServiceCost(companyId: string, assetId: string) {
  const refresh = useRefresh(assetId);
  return useMutation({
    mutationFn: async (c: { recordId: string; amount: string; currency: string }) => {
      const { error } = await supabase()
        .from('asset_service_costs')
        .upsert(
          {
            company_id: companyId,
            asset_service_record_id: c.recordId,
            amount: Number(c.amount),
            currency: c.currency || 'MYR',
          },
          { onConflict: 'asset_service_record_id' },
        );
      fail(error);
    },
    onSettled: refresh,
  });
}

export function useSaveServiceRule(companyId: string) {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: async (r: Omit<ServiceRule, 'id'> & { id?: string }) => {
      const row = {
        task: r.task.trim(),
        interval_days: r.interval_days,
        interval_deployed_days: r.interval_deployed_days,
      };
      const { error } = r.id
        ? await supabase().from('category_service_rules').update(row).eq('id', r.id)
        : await supabase()
            .from('category_service_rules')
            .insert({ ...row, company_id: companyId, category_id: r.category_id });
      fail(error);
    },
    onSettled: refresh,
  });
}

export function useDeleteServiceRule() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase().from('category_service_rules').delete().eq('id', id);
      fail(error);
    },
    onSettled: refresh,
  });
}
